import { useState, useEffect, useMemo } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MDBox } from '../../../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../../../Component/Common/Components/MDTypography';
import { DataTable } from '../../../../../Component/Common/Components/DataTable/DataTable';
import { FilterBar, FilterValues } from '../../../../../Component/Common/Components/FilterBar/FilterBar';
import { IFilter, IFilterValue, FilterType } from '../../../../../Component/Common/Components/DataTable/Filters';
import { ColumnSortOption, SortDirections } from '../../../../../Component/Model/Dto/ColumnSortOption';
import { EntriesPerPage } from '../../../../../Component/Model/Table/Table';
import { MovementTypes, RaceTypes, Gender } from '../../../../../Component/Model/Enum/BEE123Enums';
import { DeviationsDto } from '../../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import colors from '../../../../../assets/theme/base/colors';
import { raceDisplayNames, genderDisplayNames, movementTypeDisplayNames } from '../../Shared/deviationsCommon';
import { PriorityRankModal } from '../../Summary/PriorityRankModal';
import { RecruitmentPromotionMovementsDataTableColumns } from './RecruitmentPromotionMovementsDataTableColumns';
import { PriorityBreakdownModal } from '../PriorityBreakdownModal';
import { DeviationRelativeToPlanModal } from '../DeviationRelativeToPlanModal';
import { JustificationExpectationModal } from '../JustificationExpectationModal';
import { sortMovements } from './recruitmentPromotionMovementsHelper';
import { IndividualJustificationModal } from './IndividualJustificationModal';
import { IndividualJustificationStatsSummary } from './IndividualJustificationStatsSummary';
import { DeviationsService } from '../../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService';

interface RecruitmentPromotionMovementsTableProps {
    scorecardId: string;
    occupationalLevelId: number;
    occupationalLevelDisplayName: string;
    onJustificationsChanged?: () => void;
}

const movementTypeFilterValues: IFilterValue[] = [
    { key: movementTypeDisplayNames[MovementTypes.Recruitment], val: MovementTypes.Recruitment },
    { key: movementTypeDisplayNames[MovementTypes.Promotion], val: MovementTypes.Promotion },
];

const raceFilterValues: IFilterValue[] = [RaceTypes.African, RaceTypes.Coloured, RaceTypes.Indian, RaceTypes.White, RaceTypes.NonRSA]
    .map(raceId => ({ key: raceDisplayNames[raceId], val: raceId }));

const genderFilterValues: IFilterValue[] = [Gender.Male, Gender.Female]
    .map(genderId => ({ key: genderDisplayNames[genderId], val: genderId }));

const justificationStatusCaptured = 1;
const justificationStatusMissing = 2;

const justificationFilterValues: IFilterValue[] = [
    { key: 'Captured', val: justificationStatusCaptured },
    { key: 'Missing', val: justificationStatusMissing },
];

const filters: IFilter[] = [
    { label: 'Movement Type', type: FilterType.multiselect, propname: 'movementType', values: movementTypeFilterValues },
    { label: 'Race', type: FilterType.multiselect, propname: 'race', values: raceFilterValues },
    { label: 'Gender', type: FilterType.multiselect, propname: 'gender', values: genderFilterValues },
    { label: 'Justification', type: FilterType.multiselect, propname: 'justification', values: justificationFilterValues },
];

const defaultSortColumn: string = 'MovementDate';
const defaultSortDirection: SortDirections = SortDirections.DESC;

export function RecruitmentPromotionMovementsTable({ scorecardId, occupationalLevelId, occupationalLevelDisplayName, onJustificationsChanged }: RecruitmentPromotionMovementsTableProps) {
    const [movements, setMovements] = useState<DeviationsDto.RecruitmentPromotionMovementDto[]>([]);
    const [needingJustificationCount, setNeedingJustificationCount] = useState<number>(0);
    const [capturedCount, setCapturedCount] = useState<number>(0);
    const [outstandingCount, setOutstandingCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 5 });
    const [columnSort, setColumnSort] = useState<ColumnSortOption | null>(null);
    const [filterProps, setFilterProps] = useState<{ [key: string]: number[] }>({});
    const [searchValue, setSearchValue] = useState<string>('');

    const [priorityModalMovement, setPriorityModalMovement] = useState<DeviationsDto.RecruitmentPromotionMovementDto | null>(null);
    const [showMethodologyModal, setShowMethodologyModal] = useState<boolean>(false);
    const [showDeviationModal, setShowDeviationModal] = useState<boolean>(false);
    const [showExpectationModal, setShowExpectationModal] = useState<boolean>(false);
    const [justificationModalMovement, setJustificationModalMovement] = useState<DeviationsDto.RecruitmentPromotionMovementDto | null>(null);
    const [refreshCounter, setRefreshCounter] = useState<number>(0);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const result = await DeviationsService.GetRecruitmentPromotionMovements({ scorecardId, occupationalLevelId, sourceScorecardIds: null });
                if (!cancelled) {
                    setMovements(result.data.movements);
                    setNeedingJustificationCount(result.data.needingJustificationCount);
                    setCapturedCount(result.data.capturedCount);
                    setOutstandingCount(result.data.outstandingCount);
                }
            } catch {
                if (!cancelled) setError('Failed to load recruitment and promotion movements.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [scorecardId, occupationalLevelId, refreshCounter]);

    const pageSize: number = entriesPerPage.defaultValue ?? 5;

    const filteredMovements = useMemo(() => {
        const movementTypeIds: number[] = filterProps['movementType'] ?? [];
        const raceIds: number[] = filterProps['race'] ?? [];
        const genderIds: number[] = filterProps['gender'] ?? [];
        const justificationStatuses: number[] = filterProps['justification'] ?? [];
        const query: string = searchValue.trim().toLowerCase();

        return movements.filter(movement => {
            if (movementTypeIds.length > 0 && !movementTypeIds.includes(movement.movementTypeId)) return false;
            if (raceIds.length > 0 && !raceIds.includes(movement.raceId)) return false;
            if (genderIds.length > 0 && !genderIds.includes(movement.genderId)) return false;
            if (justificationStatuses.length > 0) {
                const isCaptured: boolean = movement.individualJustificationId != null;
                const isMissing: boolean = movement.requiresJustification && movement.individualJustificationId == null;
                const matchesCaptured: boolean = justificationStatuses.includes(justificationStatusCaptured) && isCaptured;
                const matchesMissing: boolean = justificationStatuses.includes(justificationStatusMissing) && isMissing;
                if (!matchesCaptured && !matchesMissing) return false;
            }
            if (query && !(movement.fullName ?? '').toLowerCase().includes(query)) return false;
            return true;
        });
    }, [movements, filterProps, searchValue]);

    const sortedMovements = useMemo(
        () => sortMovements(filteredMovements, columnSort?.ColumnName ?? defaultSortColumn, columnSort?.SortDirection ?? defaultSortDirection),
        [filteredMovements, columnSort],
    );

    const pagedMovements = useMemo(
        () => sortedMovements.slice((pageNumber - 1) * pageSize, (pageNumber - 1) * pageSize + pageSize),
        [sortedMovements, pageNumber, pageSize],
    );

    const entriesPerPageChanged = (size: number) => {
        setEntriesPerPage({ defaultValue: size });
        setPageNumber(1);
    };

    const pageNumberChanged = (page: number) => {
        setPageNumber(page);
    };

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    };

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSort(sortOption);
        setPageNumber(1);
    };

    const handleFilterChange = (values: FilterValues) => {
        setFilterProps({
            movementType: ((values['movementType'] ?? []) as IFilterValue[]).map(v => v.val),
            race: ((values['race'] ?? []) as IFilterValue[]).map(v => v.val),
            gender: ((values['gender'] ?? []) as IFilterValue[]).map(v => v.val),
            justification: ((values['justification'] ?? []) as IFilterValue[]).map(v => v.val),
        });
        setPageNumber(1);
    };

    const handleJustificationCreated = () => {
        setRefreshCounter(prev => prev + 1);
        onJustificationsChanged?.();
    };

    const handleJustificationModalClosed = () => {
        setJustificationModalMovement(null);
        setRefreshCounter(prev => prev + 1);
        onJustificationsChanged?.();
    };

    const table = RecruitmentPromotionMovementsDataTableColumns(
        pagedMovements,
        setPriorityModalMovement,
        () => setShowMethodologyModal(true),
        () => setShowDeviationModal(true),
        setJustificationModalMovement,
    );

    return (
        <MDBox
            sx={{
                mt: 3,
                p: 3,
                borderRadius: '8px',
                border: `1px solid ${colors.ui.border}`,
                backgroundColor: colors.white.main,
                '& .MuiCard-root': { boxShadow: 'none', border: `1px solid ${colors.ui.border}`, borderRadius: '8px' },
            }}
        >
            <MDBox display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <MDBox display="flex" alignItems="center">
                    <MDTypography variant="h6" sx={{ color: colors.dark.main, textTransform: 'uppercase' }}>
                        Recruitment and Promotion Justifications
                    </MDTypography>
                    <Tooltip title="About these columns">
                        <IconButton size="small" onClick={() => setShowExpectationModal(true)} aria-label="About the priority and deviation columns">
                            <InfoOutlinedIcon sx={{ color: colors.ui.secondaryText }} fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </MDBox>
                <IndividualJustificationStatsSummary
                    needing={needingJustificationCount}
                    captured={capturedCount}
                    outstanding={outstandingCount}
                    isLoading={isLoading}
                />
            </MDBox>

            {error ? (
                <MDBox sx={{ textAlign: 'center', py: 3, color: 'error.main', fontSize: '0.875rem' }}>
                    {error}
                </MDBox>
            ) : (
                <>
                    <MDBox sx={{ mb: 2 }}>
                        <FilterBar filters={filters} onChange={handleFilterChange} disableCard />
                    </MDBox>

                    <DataTable
                        table={table}
                        entriesPerPage={entriesPerPage}
                        pageNumber={pageNumber}
                        filteredItemsCount={filteredMovements.length}
                        totalItemsCount={movements.length}
                        isLoading={isLoading}
                        manualSorting={true}
                        canSearch
                        showBottomMargin={false}
                        searchBoxText="Search by name..."
                        searchValueChanged={searchValueChanged}
                        entriesPerPageChanged={entriesPerPageChanged}
                        pageNumberChanged={pageNumberChanged}
                        columnSortingChanged={columnSortingChanged}
                        noDataMessage="No recruitment and promotion movements"
                    />
                </>
            )}

            <JustificationExpectationModal open={showExpectationModal} onClose={() => setShowExpectationModal(false)} />
            <PriorityRankModal open={showMethodologyModal} onClose={() => setShowMethodologyModal(false)} />
            <DeviationRelativeToPlanModal open={showDeviationModal} onClose={() => setShowDeviationModal(false)} />
            <PriorityBreakdownModal
                open={priorityModalMovement !== null}
                onClose={() => setPriorityModalMovement(null)}
                movement={priorityModalMovement}
                occupationalLevelDisplayName={occupationalLevelDisplayName}
            />
            {justificationModalMovement && (
                <IndividualJustificationModal
                    open={true}
                    scorecardId={scorecardId}
                    individualEmployeeDataId={justificationModalMovement.individualEmployeeDataId}
                    justificationId={justificationModalMovement.individualJustificationId}
                    employeeName={justificationModalMovement.fullName}
                    occupationalLevelDisplayName={occupationalLevelDisplayName}
                    onClose={handleJustificationModalClosed}
                    onCreated={handleJustificationCreated}
                    onSaved={handleJustificationModalClosed}
                    onDeleted={handleJustificationModalClosed}
                />
            )}
        </MDBox>
    );
}
